import { removeBiometricSettings } from '@afterpay/utils/biometric'
import { useToast, Heading, Skeleton, Divider, Button, Text } from '@afterpaytouch/core'
import { Permission } from '@afterpaytouch/portal-api'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import React, { FunctionComponent, useCallback } from 'react'
import { app } from '../../../../env'
import { useAmplitudeWithEnduringEventProperties } from '../../../../integrations/amplitude'
import {
  useConsumerSelector,
  useGetPermissionsQuery,
  useDeletePermissionMutation,
  useGetBiometricCredentialsQuery,
  useDeleteBiometricCredentialsMutation,
} from '../../../../state'
import { ExtendedPanel } from '../../../Panel'

const I18N_NAMESPACE = ['common', 'account']

export const ManagePermissions: FunctionComponent = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const setToast = useToast()

  const { logEvent } = useAmplitudeWithEnduringEventProperties()

  const { locale, query } = useRouter()
  const { email, phoneNumber } = useConsumerSelector()
  const PORTAL_URL = app.PORTAL_URL

  const {
    data: permissions,
    isFetching: isGetPermissionsFetching,
    isLoading: isGetPermissionsLoading,
    isError: isGetPermissionsError,
    refetch: refetchGetPermissionsQuery,
  } = useGetPermissionsQuery()

  const [deletePermission] = useDeletePermissionMutation()

  // biometric permission
  const {
    data: biometricData,
    isFetching: isGetBiometricFetching,
    isLoading: isGetBiometricLoading,
    isError: isGetBiometricError,
    refetch: refetchGetBiometricQuery,
  } = useGetBiometricCredentialsQuery()

  const [deleteBiometric] = useDeleteBiometricCredentialsMutation()

  const refetchManagePermissions = useCallback((): void => {
    if (isGetPermissionsError) {
      refetchGetPermissionsQuery()
    }
    if (isGetBiometricError) {
      refetchGetBiometricQuery()
    }
  }, [isGetPermissionsError, refetchGetPermissionsQuery, isGetBiometricError, refetchGetBiometricQuery])

  const isBiometricLoading = isGetBiometricFetching || isGetBiometricLoading
  const isLoading = isGetPermissionsFetching || isGetPermissionsLoading

  const hasBiometricPermissions = biometricData != null && biometricData?.length > 0 && !isBiometricLoading && !isGetBiometricError
  const hasOauthPermissions =
    !isLoading && !isGetPermissionsError && permissions != null && permissions?.permissions?.length > 0 && permissions?.scopeAndDescription?.length > 0
  const hasPermissions = hasOauthPermissions || hasBiometricPermissions

  const onDeleteBiometric = async (): Promise<void> => {
    logEvent('Pressed Biometric Login Turn Off')
    try {
      await deleteBiometric().unwrap()
      removeBiometricSettings(email, phoneNumber)

      setToast({
        message: t('account:tab:security:manage:permissions:toast:success', { context: 'biometric' }),
        kind: 'Success',
        toastTime: 2000,
        manualDismiss: true,
      })
    } catch (error) {
      setToast({
        message: t('account:tab:security:manage:permissions:toast:error', { context: 'biometric' }),
        kind: 'Failure',
        toastTime: 2000,
        manualDismiss: true,
      })
    }
  }

  const onDeletePermission = async (permission: Permission): Promise<void> => {
    // @ts-expect-error TODO: fix type error
    logEvent('Clicked remove access')
    try {
      await deletePermission({ params: { clientId: permission.clientId } }).unwrap()
      setToast({
        message: t('account:tab:security:manage:permissions:toast:success', { clientName: permission.clientName }),
        kind: 'Success',
        toastTime: 2000,
        manualDismiss: true,
      })
      // @ts-expect-error TODO: fix type error
      logEvent('Edit access success')
    } catch (error) {
      setToast({
        message: t('account:tab:security:manage:permissions:toast:error', { clientName: permission.clientName }),
        kind: 'Failure',
        toastTime: 2000,
        manualDismiss: true,
      })
      // @ts-expect-error TODO: fix type error
      logEvent('Edit access error')
    }
  }

  if (isGetPermissionsError || isGetBiometricError) {
    return (
      <>
        <ExtendedPanel.Error onClick={refetchManagePermissions} />
      </>
    )
  }

  return (
    <>
      <Heading size='XL'>{t('account:tab:security:manage:permissions:heading')}</Heading>
      <div className='mt-2'>
        <Text>{t('account:tab:security:manage:permissions:description')}</Text>
        <Text>{t('account:tab:security:manage:permissions:disclaimer')}</Text>
      </div>
      <div className='mt-6'>
        {!hasPermissions && (
          <Text color='Gray40'>
            {t('account:tab:security:manage:permissions:none')}
            {isLoading && <Skeleton />}
          </Text>
        )}
        {hasOauthPermissions &&
          permissions?.permissions.map((permission, idx, arr) => {
            const showDividerAfter = idx < arr.length - 1 || hasBiometricPermissions
            return (
              <React.Fragment key={permission.clientId}>
                <div>
                  <div className='flex flex-row justify-between'>
                    <Text bold>{permission.clientName}</Text>
                    <Button.Link
                      onClick={async () => await onDeletePermission(permission)}
                      aria-label={t('account:tab:security:manage:permissions:action')}
                      size='Small'
                    >
                      {t('account:tab:security:manage:permissions:action')}
                    </Button.Link>
                  </div>
                  {permission.grantedScopes.map((grantedScope) => {
                    const descriptions = permissions.scopeAndDescription.find((s) => s.scope === grantedScope)?.description
                    return (
                      <>
                        {descriptions?.map((desc) => {
                          return (
                            <>
                              <div className='mt-2'>
                                <Text color='Gray40'>{desc}</Text>
                              </div>
                            </>
                          )
                        })}
                      </>
                    )
                  })}
                </div>
                {showDividerAfter && <Divider kind='Hairline' margin='Large' />}
              </React.Fragment>
            )
          })}
        {hasBiometricPermissions && (
          <div>
            <div className='flex flex-row justify-between'>
              <Text bold>{t('account:tab:security:manage:permissions:permission.name', { context: 'biometric' })}</Text>
              <Button.Link
                onClick={async () => await onDeleteBiometric()}
                testNameSpace='biometric-deactivate'
                aria-label={t('account:tab:security:manage:permissions:action', { context: 'biometric' })}
                size='Small'
              >
                {t('account:tab:security:manage:permissions:action', { context: 'biometric' })}
              </Button.Link>
            </div>
            <div className='mt-2'>
              <Text color='Gray40'>{t('account:tab:security:manage:permissions:permission:scope:description', { context: 'biometric' })}</Text>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
